// src/features/auth/hooks/usePostLoginOnboarding.tsx
import { useState, useEffect } from 'react';
import authService from '../services/authService';

interface OnboardingState {
  show2FAPrompt: boolean;
  userHas2FA: boolean;
  onboardingComplete: boolean;
  loading: boolean;
}

interface UsePostLoginOnboardingReturn extends OnboardingState {
  handleSetup2FA: () => Promise<void>;
  handleSkip2FA: () => void;
  handleDontShowAgain: () => void;
}

const ONBOARDING_STORAGE_KEY = 'auth_onboarding_dismissed';
const ONBOARDING_TIMESTAMP_KEY = 'auth_onboarding_dismissed_timestamp';
const ONBOARDING_EXPIRATION_DAYS = 30; // Show again after 30 days (configurable)

// Helper function to check if "Don't Show Again" is still valid
const isOnboardingDismissalValid = (): boolean => {
  try {
    const dismissed = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    const dismissedTimestamp = localStorage.getItem(ONBOARDING_TIMESTAMP_KEY);
    
    if (dismissed !== 'true' || !dismissedTimestamp) {
      return false;
    }
    
    const dismissedTime = parseInt(dismissedTimestamp);
    const currentTime = Date.now();
    const expirationTime = dismissedTime + (ONBOARDING_EXPIRATION_DAYS * 24 * 60 * 60 * 1000);
    
    // If expired, clear the storage and return false
    if (currentTime > expirationTime) {
      localStorage.removeItem(ONBOARDING_STORAGE_KEY);
      localStorage.removeItem(ONBOARDING_TIMESTAMP_KEY);
      return false;
    }
    
    return true;
  } catch (error) {
    console.warn('Failed to check onboarding dismissal validity:', error);
    return false;
  }
};

export const usePostLoginOnboarding = (): UsePostLoginOnboardingReturn => {
  const [state, setState] = useState<OnboardingState>({
    show2FAPrompt: false,
    userHas2FA: false,
    onboardingComplete: false,
    loading: true
  });

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        // Check if user has dismissed onboarding and it's still valid (within 30 days)
        if (isOnboardingDismissalValid()) {
          setState(prev => ({ ...prev, onboardingComplete: true, loading: false }));
          return;
        }

        // Check user's 2FA status
        const userInfo = await authService.getUserInfo();
        let has2FA = userInfo.user.two_factor_enabled;

        // Cross-verify with dedicated 2FA status endpoint if user info is uncertain
        if (!has2FA) {
          try {
            const twoFactorStatus = await authService.get2FAStatus();
            has2FA = twoFactorStatus.is_enabled;
          } catch (error) {
            console.warn('Failed to verify 2FA status via /api/auth/2fa/status/, using user info fallback:', error);
            has2FA = userInfo.user.two_factor_enabled || false;
          }
        }

        setState(prev => ({
          ...prev,
          userHas2FA: has2FA,
          show2FAPrompt: !has2FA, // Show prompt only if 2FA is not enabled
          onboardingComplete: has2FA, // Complete if 2FA is already enabled
          loading: false
        }));
      } catch (error) {
        
        setState(prev => ({ ...prev, loading: false }));
      }
    };

    checkOnboardingStatus();
  }, []);

  const handleSetup2FA = async (): Promise<void> => {
    try {
      // This would typically navigate to 2FA setup
      // For now, we'll just close the prompt and mark as complete
      setState(prev => ({
        ...prev,
        show2FAPrompt: false,
        onboardingComplete: true
      }));
      
      // In a real implementation, you might:
      // 1. Open the 2FA setup modal/page
      // 2. Wait for completion
      // 3. Update the state accordingly
      
    } catch (error) {
      
    }
  };

  const handleSkip2FA = (): void => {
    setState(prev => ({
      ...prev,
      show2FAPrompt: false,
      onboardingComplete: true
    }));
  };

  const handleDontShowAgain = (): void => {
    try {
      // Save dismissal state with timestamp
      localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
      localStorage.setItem(ONBOARDING_TIMESTAMP_KEY, Date.now().toString());
      
      setState(prev => ({
        ...prev,
        show2FAPrompt: false,
        onboardingComplete: true
      }));
    } catch (error) {
      console.warn('Failed to save onboarding dismissal:', error);
      // Still update state even if localStorage fails
      setState(prev => ({
        ...prev,
        show2FAPrompt: false,
        onboardingComplete: true
      }));
    }
  };

  return {
    ...state,
    handleSetup2FA,
    handleSkip2FA,
    handleDontShowAgain
  };
};
